import { type RefObject, useCallback, useLayoutEffect, useRef } from 'react';

type SwiperInstance = {
    params?: { centeredSlides?: boolean; loop?: boolean };
    realIndex: number;
    once: (event: string, handler: () => void) => void;
    slideToLoop: (index: number, speed?: number, runCallbacks?: boolean) => void;
    slideNext: (speed?: number, runCallbacks?: boolean) => void;
    slidePrev: (speed?: number, runCallbacks?: boolean) => void;
    update?: () => void;
    loopFix?: () => void;
};

type SwiperElement = HTMLElement & {
    swiper?: SwiperInstance;
};

type UseSwiperSyncOptions = {
    readonly activeSlideIndex: number;
    readonly slidesCount: number;
    readonly slideKeys: string;
    readonly onSlideChange: (index: number) => void;
    readonly forceCenteredSlides?: boolean;
};

const SYNC_POLL_MS = 50; // LazySlider mounts Swiper asynchronously
const SYNC_RESET_MS = 500; // Fallback if slideChangeTransitionEnd never fires

const getSwiper = (container: HTMLDivElement | null): SwiperInstance | undefined => {
    const swiperEl = container?.querySelector('.swiper') as SwiperElement | null;
    return swiperEl?.swiper;
};

const moveToIndex = (swiper: SwiperInstance, index: number, slidesCount: number): void => {
    const step = slidesCount > 0 ? (index - swiper.realIndex + slidesCount) % slidesCount : 0;

    if (step === 1) {
        swiper.slideNext(undefined, false);
        return;
    }

    if (slidesCount > 1 && step === slidesCount - 1) {
        swiper.slidePrev(undefined, false);
        return;
    }

    swiper.slideToLoop(index, undefined, false);
};

/**
 Main and Thumbnail Sliders each have their own Swiper. When one changes, this hook
 moves the other so both show the same motif.
 
 Avoids:
 - sliders showing different motifs
 - switch_motif tracking for our own slideTo (not a user click)
 - thumbnail slideTo calling onSlideChange and fighting parent state
 - missing the first sync because Swiper is not mounted yet (LazySlider)
 - isSyncing staying true if the transition event never fires
 - leftover timeouts from fast motif changes
 */
const useSwiperSync = (
    containerRef: RefObject<HTMLDivElement | null>,
    {
        activeSlideIndex,
        forceCenteredSlides = false,
        onSlideChange,
        slideKeys,
        slidesCount,
    }: UseSwiperSyncOptions,
): { isSyncingRef: RefObject<boolean>; onSwiperSlideChange: (index: number) => void } => {
    const isSyncingRef = useRef(false);
    const resetTimeoutRef = useRef<number | undefined>(undefined);

    useLayoutEffect(() => {
        let intervalId: number | undefined;

        const resetSyncing = (): void => {
            isSyncingRef.current = false;
        };

        const syncSwiper = (): boolean => {
            const swiper = getSwiper(containerRef.current);

            if (!swiper) {
                return false;
            }

            const needsCenteredSlides = Boolean(
                forceCenteredSlides && swiper.params && !swiper.params.centeredSlides,
            );

            if (needsCenteredSlides && swiper.params && swiper.update) {
                swiper.params.centeredSlides = true;
                swiper.update();
                swiper.loopFix?.();
            }

            if (swiper.realIndex !== activeSlideIndex) {
                isSyncingRef.current = true;

                if (resetTimeoutRef.current) {
                    window.clearTimeout(resetTimeoutRef.current);
                }

                swiper.once('slideChangeTransitionEnd', resetSyncing);
                resetTimeoutRef.current = window.setTimeout(resetSyncing, SYNC_RESET_MS);
                moveToIndex(swiper, activeSlideIndex, slidesCount);
            }

            return true;
        };

        if (!syncSwiper()) {
            intervalId = window.setInterval(() => {
                if (syncSwiper() && intervalId) {
                    window.clearInterval(intervalId);
                }
            }, SYNC_POLL_MS);
        }

        return () => {
            if (intervalId) {
                window.clearInterval(intervalId);
            }

            if (resetTimeoutRef.current) {
                window.clearTimeout(resetTimeoutRef.current);
            }
        };
    }, [activeSlideIndex, containerRef, forceCenteredSlides, slideKeys, slidesCount]);

    const onSwiperSlideChange = useCallback(
        (index: number): void => {
            if (!isSyncingRef.current) {
                onSlideChange(index);
            }
        },
        [onSlideChange],
    );

    return { isSyncingRef, onSwiperSlideChange };
};

export { useSwiperSync };
